import { useEffect, useState } from "react";
import { Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/KpiCard";
import { useFilters } from "@/context/FilterContext";
import { api, errMsg, money } from "@/lib/api";

const VIDE = { motcle: "", montant: "", tolerance: "25", logements: [], nouveau: "" };

const RegleItem = ({ r, onDelete }) => (
  <li className="flex items-start justify-between gap-3 py-2.5" data-testid={`regle-row-${r.id}`}>
    <div className="min-w-0">
      <p className="text-sm"><span className="font-mono font-semibold">« {r.motcle} »</span>
        {r.montant != null && <span className="text-slate-500"> · montant ≈ {money(r.montant)} (± {money(r.tolerance)})</span>}</p>
      <p className="text-xs text-slate-600">→ {r.logements.join(" + ")}{r.logements.length > 1 ? " (réparti à parts égales)" : ""} · {r.nb_operations} opération(s)</p>
    </div>
    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => onDelete(r)} data-testid={`regle-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
  </li>
);

const RegleForm = ({ logements, onSaved }) => {
  const [f, setF] = useState(VIDE);
  const [saving, setSaving] = useState(false);
  const toggle = (l) => setF({ ...f, logements: f.logements.includes(l) ? f.logements.filter((x) => x !== l) : [...f.logements, l] });
  const save = async () => {
    setSaving(true);
    const logs = [...f.logements, ...(f.nouveau.trim() ? [f.nouveau.trim()] : [])];
    try {
      const { data } = await api.post("/regles", { motcle: f.motcle, montant: f.montant ? Number(f.montant) : null, tolerance: Number(f.tolerance || 0), logements: logs });
      toast.success(`Règle enregistrée · ${data.affectees} opération(s) affectée(s)`);
      setF(VIDE); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="regle-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1"><Label className="text-xs">La description contient</Label>
          <Input value={f.motcle} onChange={(e) => setF({ ...f, motcle: e.target.value })} placeholder="HYDRO" className="font-mono uppercase" data-testid="regle-motcle-input" /></div>
        <div className="space-y-1"><Label className="text-xs">Montant environ (facultatif)</Label>
          <Input type="number" min="0" step="0.01" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} placeholder="600" data-testid="regle-montant-input" /></div>
        <div className="space-y-1"><Label className="text-xs">Écart accepté ± $</Label>
          <Input type="number" min="0" value={f.tolerance} disabled={!f.montant} onChange={(e) => setF({ ...f, tolerance: e.target.value })} data-testid="regle-tolerance-input" /></div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Charge de (plusieurs = répartie à parts égales)</Label>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {logements.map((l) => (
            <label key={l} className="flex items-center gap-2 text-sm"><Checkbox checked={f.logements.includes(l)} onCheckedChange={() => toggle(l)} data-testid={`regle-logement-${l}`} />{l}</label>
          ))}
        </div>
        <Input value={f.nouveau} onChange={(e) => setF({ ...f, nouveau: e.target.value })} placeholder="ou un autre logement : Appartement Notre-Dame" className="h-9" data-testid="regle-nouveau-logement-input" />
      </div>
      <Button size="sm" onClick={save} disabled={saving || f.motcle.trim().length < 2 || (!f.logements.length && !f.nouveau.trim())} className="bg-emerald-700 hover:bg-emerald-800" data-testid="regle-save-button">
        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}Ajouter la règle
      </Button>
    </div>
  );
};

export const ReglesPanel = () => {
  const { options, refreshOptions } = useFilters();
  const [regles, setRegles] = useState([]);
  const load = () => api.get("/regles").then((r) => setRegles(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const reload = () => { load(); refreshOptions(); };
  const del = async (r) => {
    if (!window.confirm(`Supprimer la règle « ${r.motcle} » ? Ses opérations redeviennent non affectées.`)) return;
    try { await api.delete(`/regles/${r.id}`); toast.success("Règle supprimée"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async () => {
    try { const { data } = await api.post("/regles/appliquer"); toast.success(`${data.affectees} opération(s) affectée(s)`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Règles d'affectation des charges" testId="regles-panel"
      action={<Button size="sm" variant="outline" onClick={run} data-testid="regles-apply-button"><RefreshCw className="h-3.5 w-3.5 mr-1" />Appliquer maintenant</Button>}>
      <p className="text-xs text-slate-500 mb-4">Chaque débit bancaire non affecté dont la description contient le mot-clé est affecté automatiquement, à l'import et aux opérations déjà importées. Une règle avec montant passe avant une règle sans montant. Vos affectations manuelles ne sont jamais modifiées.</p>
      <RegleForm logements={options.logements} onSaved={reload} />
      {regles.length === 0 ? <p className="mt-4 text-sm text-slate-500">Aucune règle pour l'instant.</p> : (
        <ul className="mt-3 divide-y divide-slate-100" data-testid="regles-list">{regles.map((r) => <RegleItem key={r.id} r={r} onDelete={del} />)}</ul>
      )}
    </Panel>
  );
};
